import type { KlearDate } from '@klear/date'
import type { Money } from '@klear/money'
import { trpc, type RouterOutputs } from '@klear/ui/lib/trpc'
import { useMutationWithToast } from '@klear/ui/hooks/use-mutations-with-toast'

const PAAS_POLL_INTERVAL_MS = 8000

export type PaasPositionRow = RouterOutputs['collection']['listPaas'][number]

export type PaasDisbursableRow = RouterOutputs['disbursement']['listPaasDisbursables'][number]

export type PaasAllocationRow = {
  allocationId: number
  supplierPaymentId: number
  paasIdDisplay: string
  traderOrganizationName: string | null
  paymentId: number
  paymentTypeLabel: string
  allocated: Money
  paymentAmount: Money
  status: string
  effectiveDate: KlearDate | undefined
}

export const PAAS_SCOPE = {
  OUTSTANDING: 'outstanding',
  DRAFT: 'draft',
  COLLECTED: 'collected',
  ALL: 'all'
} as const

export type PaasScope = (typeof PAAS_SCOPE)[keyof typeof PAAS_SCOPE]

export const PAAS_SCOPE_OPTIONS: { value: PaasScope; label: string }[] = [
  { value: PAAS_SCOPE.OUTSTANDING, label: 'Outstanding' },
  { value: PAAS_SCOPE.DRAFT, label: 'Draft' },
  { value: PAAS_SCOPE.COLLECTED, label: 'Collected' },
  { value: PAAS_SCOPE.ALL, label: 'All' }
]

export function paasScopeFilters(scope: PaasScope): { collected?: boolean; draft?: boolean } {
  if (scope === PAAS_SCOPE.OUTSTANDING) return { collected: false }
  if (scope === PAAS_SCOPE.DRAFT) return { draft: true }
  if (scope === PAAS_SCOPE.COLLECTED) return { collected: true }
  return {}
}

export function usePaasCollections(
  { collected, draft, traderId }: { collected?: boolean; draft?: boolean; traderId?: number } = {},
  enabled = true
) {
  const { data, isLoading } = trpc.collection.listPaas.useQuery(
    { filters: { collected, draft, traderId } },
    { enabled, refetchInterval: PAAS_POLL_INTERVAL_MS }
  )
  return { positions: data ?? [], isLoading }
}

export function usePaasPositions(
  { collected, draft, traderId }: { collected?: boolean; draft?: boolean; traderId?: number } = {},
  enabled = true
) {
  const { data, isLoading } = trpc.disbursement.listPaasPositions.useQuery(
    { filters: { collected, draft, traderId } },
    { enabled, refetchInterval: PAAS_POLL_INTERVAL_MS }
  )
  return { positions: data ?? [], isLoading }
}

export function usePaasFunders(enabled = true) {
  const { data, isLoading } = trpc.funder.list.useQuery({}, { enabled })
  return { funders: data ?? [], isLoading }
}

export function usePaasDisbursables({ traderId }: { traderId?: number } = {}, enabled = true) {
  const { data, isLoading } = trpc.disbursement.listPaasDisbursables.useQuery(
    { filters: { traderId } },
    { enabled, refetchInterval: PAAS_POLL_INTERVAL_MS }
  )
  return { disbursables: data ?? [], isLoading }
}

export function paasAllocationRows(positions: PaasPositionRow[]): PaasAllocationRow[] {
  return positions.flatMap((position) =>
    position.allocations.map((allocation) => ({
      allocationId: allocation.id,
      supplierPaymentId: position.supplierPayment.id,
      paasIdDisplay: position.idDisplay,
      traderOrganizationName: position.traderOrganizationName,
      paymentId: allocation.payment.id,
      paymentTypeLabel: allocation.payment.paymentTypeLabel,
      allocated: allocation.allocated,
      paymentAmount: allocation.payment.amount,
      status: allocation.payment.status,
      effectiveDate: allocation.payment.effectiveDate
    }))
  )
}

export function usePaasMutations() {
  const utils = trpc.useUtils()
  const invalidate = () => {
    utils.collection.listPaas.invalidate()
    utils.disbursement.listPaasPositions.invalidate()
    utils.disbursement.listPaasDisbursables.invalidate()
  }

  const createAdvanceMutation = useMutationWithToast(
    trpc.disbursement.createPaasAdvance.useMutation,
    { successTitle: 'Draft PaaS advance created', errorTitle: 'Failed to create the advance' },
    { onSuccess: invalidate }
  )
  const disburseMutation = useMutationWithToast(
    trpc.disbursement.disbursePaas.useMutation,
    { successTitle: 'PaaS advance disbursed', errorTitle: 'Failed to disburse the advance' },
    { onSuccess: invalidate }
  )
  const collectMutation = useMutationWithToast(
    trpc.paas.collect.useMutation,
    { successTitle: 'PaaS collection sent', errorTitle: 'Failed to collect' },
    { onSuccess: invalidate }
  )
  const allocateMutation = useMutationWithToast(
    trpc.paas.allocate.useMutation,
    { successTitle: 'Payment allocated', errorTitle: 'Failed to allocate the payment' },
    { onSuccess: invalidate }
  )
  const deallocateMutation = useMutationWithToast(
    trpc.paas.deallocate.useMutation,
    { successTitle: 'Allocation removed', errorTitle: 'Failed to remove the allocation' },
    { onSuccess: invalidate }
  )
  const setExpectedFeeMutation = useMutationWithToast(
    trpc.paas.setExpectedFee.useMutation,
    { successTitle: 'Expected PaaS fee saved', errorTitle: 'Failed to save the expected fee' },
    { onSuccess: invalidate }
  )

  return {
    collect: collectMutation.mutateAsync,
    isCollecting: collectMutation.isPending,
    createAdvance: createAdvanceMutation.mutateAsync,
    isCreatingAdvance: createAdvanceMutation.isPending,
    disburse: disburseMutation.mutateAsync,
    isDisbursing: disburseMutation.isPending,
    allocate: allocateMutation.mutateAsync,
    isAllocating: allocateMutation.isPending,
    deallocate: deallocateMutation.mutateAsync,
    isDeallocating: deallocateMutation.isPending,
    setExpectedFee: setExpectedFeeMutation.mutateAsync,
    isSavingExpectedFee: setExpectedFeeMutation.isPending
  }
}
